<script setup lang="ts">
import { defineAsyncComponent, computed, ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import isEmpty from 'lodash/isEmpty'
import { useHead, useDevice } from '#imports'
import { SHOW_MOBILE_HR_BANNER_URL, IGNORE_MOBILE_HEADER_URL, ACCOUNT_PATH, PROMOTION_PATH } from '~/constants'
import { EVENT, IGNORE_MOBILE_FOOTER_URL, PAGE_NOT_FOUND } from '~/configs/page-url'
import { useLogin } from '~/composables/account/use-login'
import { useResetCookie } from '~/composables/common/use-reset-cookie'
import { useUserData } from '~/composables/account/use-user-data'
import { useDepositData } from '~composables/deposit/use-deposit-data'
import { useMaintain } from '~/composables/common/use-maintain'
import { useContact } from '~/composables/common/use-contact'
import { useModalStore } from '~/stores/modal.store.ts'
import { useSocket } from '~/composables/common/use-socket'
import { useSyncAuthenticateDataTab } from '~/composables/common/use-sync-authenticate-tab'
import { useLangdingPageLogin } from '~/composables/common/use-landing-page-login'
import { useRegisterAddon } from '~/composables/common/use-register-addon'
import { toastPopup } from '~/utils/modal/toast'
import { useLighthouse } from '~/composables/common/use-lighthouse'

const BannerSlider = defineAsyncComponent(async () => await import('~/components/mobile/menu-game/hero-banner.vue'))
const MenuSports = defineAsyncComponent(async () => await import('~/components/mobile/menu-game/menu-sports.vue'))
const MenuCategoryGames = defineAsyncComponent(async () => await import('~/components/mobile/menu-game/index.vue'))
const ScrollTopButton = defineAsyncComponent(
  async () => await import('~/components/mobile/common/scroll-top-button.vue')
)
const ModalDisplayedVersion = defineAsyncComponent(
  async () => await import('~/components/common/modal-displayed-version.vue')
)
const ModalMenu = defineAsyncComponent(async () => await import('~/components/mobile/layouts/modal-menu.vue'))
const ModalUpdateDisplayName = defineAsyncComponent(
  async () => await import('~/components/mobile/auth/modal-update-diplayname.vue')
)
const AppHeader = defineAsyncComponent(async () => await import('~/components/mobile/layouts/header.vue'))
const AppFooter = defineAsyncComponent(async () => await import('~/components/mobile/layouts/footer.vue'))
const ModalLoginSignUp = defineAsyncComponent(
  async () => await import('~/components/mobile/auth/modal-login-sign-up.vue')
)
const ModalAccount = defineAsyncComponent(
  async () => await import('~/components/mobile/pages/account/modal-account-section.vue')
)
const ModalForgotPassword = defineAsyncComponent(
  async () => await import('~/components/mobile/auth/modal-forgot-password.vue')
)
const ModalResetPassword = defineAsyncComponent(
  async () => await import('~/components/mobile/auth/modal-reset-password.vue')
)

const Modal500k = defineAsyncComponent(async () => await import('~/components/common/modal-500k.vue'))
const FloatingEvent = defineAsyncComponent(async () => await import('~/components/common/event/floating.vue'))
const WelcomePopup = defineAsyncComponent(async () => await import('~/components/common/event/welcome-popup.vue'))
const FloatingEventHalloween = defineAsyncComponent(async () => await import('~/components/common/event/halloween/floating.vue'))
const EventEntryHalloween = defineAsyncComponent(async () => await import('~/components/common/event/halloween/event-entry.vue'))

const { isLighthouseBot } = useLighthouse()
const store = useModalStore()
const route = useRoute()
const { isLogged, openAuthModalByQuery } = useLogin()
const { isWindows, isMacOS, isChrome } = useDevice()
const { resetCookie } = useResetCookie()
const { setMaintenanceGames } = useMaintain()
const { fetchContact } = useContact()
const { initListAgency } = useDepositData()
const {
  EVENT_PREMIER_LEAGUE_RANKING_ENABLED,
  EVENT_PREMIER_LEAGUE_RANKING_POPUP_ENABLED,
  EVENT_HALLOWEEN_ENABLE,
  EVENT_HALLOWEEN_POPUP_ENABLE
} = useRuntimeConfig().public

const isHiddenEvent = computed(() => {
  return route.path === PAGE_NOT_FOUND || route.path === EVENT.PREMIER_LEAGUE_RANKING
})

const isHiddenEventHalloween = computed(() => {
  return route.path === PAGE_NOT_FOUND || route.path === EVENT.HALLOWEEN
})

if (!isLighthouseBot) {
  fetchContact()
  useSyncAuthenticateDataTab()
  setMaintenanceGames()
  useLangdingPageLogin()
  useRegisterAddon()
}

if (isLogged.value) {
  initListAgency()
}
const pageName = ref()
const { currentUser } = useUserData()
useHead({
  bodyAttrs: {
    class: computed(() => {
      const bodyClass = []

      if (store.items.length) {
        bodyClass.push('modal-open modal-mobile')
      }
      if (pageName.value) {
        bodyClass.push(`page-${pageName.value}`)
      }

      return bodyClass.join(' ')
    })
  }
})
onMounted(() => {
  if (!isLighthouseBot) {
    useSocket()
    if (sessionStorage.getItem('changePassword')) {
      sessionStorage.removeItem('changePassword')
      toastPopup({ title: 'Cập nhập thông tin thành công', type: 'success' })
    }

    openAuthModalByQuery()
    watch(
      () => currentUser.value.token,
      newToken => {
        if (!newToken) {
          resetCookie()
        }
      },
      { immediate: true }
    )
  }
})

const isHeaderShown = computed((): boolean => {
  if (route.path.includes('.html')) {
    return false
  }
  return !IGNORE_MOBILE_HEADER_URL.includes(route.path)
})

const isShowFooter = computed((): boolean => {
  if (route.path.includes('.html')) {
    return false
  }
  if (route.path === ACCOUNT_PATH) {
    return isEmpty(route.query)
  }
  if (IGNORE_MOBILE_FOOTER_URL.includes(route.path)) {
    return false
  }
  if (route.path.includes(PROMOTION_PATH)) {
    const query = route.query
    if (query && query.tab !== undefined) {
      return false
    }
  }
  return true
})

const showMenuCategoryGames = computed((): boolean => {
  return SHOW_MOBILE_HR_BANNER_URL.includes(route.path)
})
const currentUrl = computed(() => route.path.replace('/', ''))
</script>
<template>
  <div
    class="layout-mobile position-relative"
    :class="[{ windows: isWindows }, { 'mac-os': isMacOS }, { chrome: isChrome }, currentUrl]"
  >
    <AppHeader v-if="isHeaderShown" />
    <main class="main-page-mobile">
      <template v-if="showMenuCategoryGames">
        <BannerSlider />
        <MenuSports />
        <MenuCategoryGames />
      </template>
      <slot></slot>
    </main>
    <template v-if="!isLighthouseBot">
      <ModalLoginSignUp />
      <ModalForgotPassword />
      <ModalResetPassword />
      <ModalDisplayedVersion />
      <ModalMenu />
      <ModalUpdateDisplayName />
      <Modal500k />
      <ScrollTopButton />
      <ClientOnly>
        <template v-if="EVENT_PREMIER_LEAGUE_RANKING_ENABLED && !isHiddenEvent">
          <FloatingEvent />
          <WelcomePopup v-if="EVENT_PREMIER_LEAGUE_RANKING_POPUP_ENABLED" />
        </template>
        <template v-if="EVENT_HALLOWEEN_ENABLE && !isHiddenEventHalloween">
          <FloatingEventHalloween />
          <EventEntryHalloween v-if="EVENT_HALLOWEEN_POPUP_ENABLE" />
        </template>
      </ClientOnly>

      <ClientOnly>
        <ModalAccount v-if="isLogged" />
        <AppFooter v-if="isShowFooter" />
      </ClientOnly>
    </template>
  </div>
</template>

<style scoped lang="scss">
@import '~scssAsset/layout/mobile.scss';
</style>
